import type { SurfaceSnapshot } from "./types";

const TOKEN_KEY = "surface:token";
const BASE_KEY = "surface:base_url";

export interface AuthConfig {
  baseUrl: string;
  token: string;
}

export function getAuth(): AuthConfig | null {
  const baseUrl = localStorage.getItem(BASE_KEY);
  const token = localStorage.getItem(TOKEN_KEY);
  if (!baseUrl || !token) return null;
  return { baseUrl: baseUrl.replace(/\/$/, ""), token };
}

export function setAuth(baseUrl: string, token: string): void {
  localStorage.setItem(BASE_KEY, baseUrl.replace(/\/$/, ""));
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(BASE_KEY);
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const auth = getAuth();
  if (!auth) throw new Error("not_configured");
  const r = await fetch(`${auth.baseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${auth.token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });
  if (r.status === 401) throw new Error("unauthorized");
  return r;
}

export async function fetchState(opts: { fresh?: boolean } = {}): Promise<SurfaceSnapshot> {
  const r = await authedFetch(`/surface/state${opts.fresh ? "?fresh=1" : ""}`);
  if (!r.ok) {
    let detail = "";
    try { detail = (await r.json()).detail || ""; } catch {}
    throw new Error(`fetch_failed:${r.status}${detail ? `:${detail}` : ""}`);
  }
  return r.json();
}

export interface CompletePayload {
  file: string;
  line: number;
  text?: string;
  gt_account?: string | null;
  gt_id?: string | null;
  target_state: "done" | "open";
}

export async function completeTask(p: CompletePayload): Promise<{ ok: boolean; state: string; google?: unknown }> {
  const r = await authedFetch("/surface/complete", {
    method: "POST",
    body: JSON.stringify(p),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `complete_failed:${r.status}`);
  return data;
}

export interface QuickAddPayload {
  text: string;
  account: "personal" | "work";
  project_slug?: string;
  file_path?: string;
  section?: string;
}

export async function quickAdd(p: QuickAddPayload): Promise<{ ok: boolean; fingerprint: string | null; gt_id: string }> {
  const r = await authedFetch("/surface/quick-add", {
    method: "POST",
    body: JSON.stringify(p),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `quick_add_failed:${r.status}`);
  return data;
}

export async function pinToToday(p: { fingerprint: string; evening?: boolean; day?: string }): Promise<{ ok: boolean; day: string }> {
  const r = await authedFetch("/surface/today/pin", {
    method: "POST",
    body: JSON.stringify(p),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `pin_failed:${r.status}`);
  return data;
}

export async function unpinFromToday(p: { fingerprint: string }): Promise<{ ok: boolean }> {
  const r = await authedFetch("/surface/today/unpin", {
    method: "POST",
    body: JSON.stringify(p),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `unpin_failed:${r.status}`);
  return data;
}

export async function fileGtTask(p: { gt_account: string; gt_id: string; project_slug: string; section?: string }): Promise<{ ok: boolean; file: string }> {
  const r = await authedFetch("/surface/file-task", {
    method: "POST",
    body: JSON.stringify(p),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.error || `file_task_failed:${r.status}`);
  return data;
}

export async function fetchServerBuild(): Promise<string | null> {
  const auth = getAuth();
  if (!auth) return null;
  try {
    const r = await fetch(`${auth.baseUrl}/surface/build`);
    if (!r.ok) return null;
    const data = await r.json();
    return typeof data?.build === "string" ? data.build : null;
  } catch { return null; }
}

const HARD_RELOAD_FLAG = "surface:hardreload-fired";

export function consumeHardReloadFlag(): string | null {
  // Returns the build the reload was attempting to clear *from*, then clears the flag.
  const v = localStorage.getItem(HARD_RELOAD_FLAG);
  if (v) localStorage.removeItem(HARD_RELOAD_FLAG);
  return v;
}

export async function hardReload(currentBuild: string): Promise<void> {
  // Mark the flag so the post-reload page can show a confirmation toast.
  try { localStorage.setItem(HARD_RELOAD_FLAG, currentBuild); } catch {}
  // Unregister all service workers + nuke caches, then full-reload.
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn("hardReload cleanup failed:", e);
  }
  // Use a cache-busting query string so iOS can't serve a stale HTML.
  const u = new URL(window.location.href);
  u.searchParams.set("_v", Date.now().toString());
  window.location.replace(u.toString());
}
